import { ActionReducer, MetaReducer, INIT } from '@ngrx/store';
import * as userActions from '../actions/user.action';

export const STORAGE_KEY = 'card4games_shop_state';

/**
 * Meta-reducer that persists specified slices of NgRx store state into localStorage
 * and rehydrates them when the application initializes.
 *
 * - Rehydrates only on INIT.
 * - On LOGOUT: clears the given slices from both the store and localStorage.
 * - Safe for Angular SSR.
 */
export function localStorageSyncMetaReducer<T = any>(
  storageKey: string = STORAGE_KEY,
  keysToPersist: string[] = [],
  keysToClearOnLogout: string[] = []
): MetaReducer<T> {
  return (reducer: ActionReducer<T>): ActionReducer<T> => {
    return (state: T | undefined, action: any): T => {
      const isBrowser = typeof window !== 'undefined' && !!window.localStorage;
      const isLogout = action?.type === userActions.LOGOUT.type;

      // 1) LOGOUT: امسح الـ slices من الـ localStorage ومن الـ state
      if (isLogout) {
        if (isBrowser) {
          try {
            const stored = localStorage.getItem(storageKey);
            if (stored) {
              const parsed = JSON.parse(stored);
              keysToClearOnLogout.forEach((k) => delete parsed[k]);
              localStorage.setItem(storageKey, JSON.stringify(parsed));
            }
          } catch (error) {
            console.error('[localStorageSyncMetaReducer] Error clearing storage:', error);
          }
        }

        if (state) {
          const cleaned: any = { ...state };
          keysToClearOnLogout.forEach((k) => delete cleaned[k]);
          state = cleaned; // الـ reducer هيرجّع initialState للـ slices المحذوفة
        }
      }

      let nextState = reducer(state, action);

      if (!isBrowser) {
        return nextState;
      }

      // 2) Rehydrate: عند INIT بس
      if (action?.type === INIT) {
        try {
          const storedState = localStorage.getItem(storageKey);
          if (storedState) {
            const parsed = JSON.parse(storedState);
            const picked: any = {};
            keysToPersist.forEach((k) => {
              if (parsed[k] !== undefined) picked[k] = parsed[k];
            });
            nextState = { ...nextState, ...picked };
          }
        } catch (error) {
          console.error('[localStorageSyncMetaReducer] Error rehydrating:', error);
        }
      }

      // 3) Save
      try {
        if (nextState) {
          const keys =
            keysToPersist.length > 0 ? keysToPersist : Object.keys(nextState as object);

          const stateToSave: Record<string, any> = {};
          keys.forEach((key) => {
            if ((nextState as any)[key] !== undefined) {
              stateToSave[key] = (nextState as any)[key];
            }
          });

          localStorage.setItem(storageKey, JSON.stringify(stateToSave));
        }
      } catch (error) {
        console.error('[localStorageSyncMetaReducer] Error saving:', error);
      }

      // Debug: اعرف مين الـ action اللي بيرجّع الـ cart بعد الـ logout (شيله بعد ما تخلص)
      const cart = (nextState as any)?.cart;
      if (!isLogout && cart?.items?.length) {
        console.log('Cart saved after action:', action?.type, cart);
      }

      return nextState;
    };
  };
}

export const metaReducers: MetaReducer<any>[] = [
  localStorageSyncMetaReducer(
    STORAGE_KEY,
    ['regionalSetting', 'cart', 'navbar'], // اللي بيتحفظ
    ['cart']                               // اللي يتمسح عند الـ logout
  ),
];